import { DEMO_CONFIGS } from '../app/demos/config';
import { stripTrailingSlash } from './url';

const SITE_URL = 'https://upcaret.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const HOME_OG_IMAGE = `${SITE_URL}/home-og.png`;
const FREE_AI_AUDIT_OG_IMAGE = `${SITE_URL}/free-ai-audit-og.png`;

type SeoEntry = {
  title: string;
  description: string;
  ogType?: string;
  ogImage?: string;
  ogImageAlt?: string;
};

const defaultEntry: SeoEntry = {
  title: 'upCaret: AI Automation Reviews & Practical Workflow Builds',
  description:
    'Find the manual work costing you time, leads, or cash, then build practical AI automation and workflow systems that save time and hold up in production.',
  ogType: 'website',
};

const seoEntries: Record<string, SeoEntry> = {
  '/': {
    title: 'upCaret: Find the Manual Work Costing Your Team Time',
    description:
      'Book a free 15-minute AI workflow audit. We identify one bottleneck, estimate what it may be costing you, and recommend the next practical automation step.',
    ogType: 'website',
    ogImage: HOME_OG_IMAGE,
    ogImageAlt:
      'upCaret homepage card: find the manual work costing your team time and book a free 15-minute call',
  },
  '/products': {
    title: 'Automation Services for Small Teams',
    description:
      'Start with the Automation Review to rank your best opportunities, then build the first high-value workflow or keep improving with ongoing monthly support.',
  },
  '/products/agent': {
    title: 'AI Customer Support Agent',
    description:
      'Get an AI agent that knows your content, answers customers 24/7, and books demos. Your data stays private, always.',
  },
  '/products/agent/how-it-works': {
    title: 'How We Train Your AI Agent',
    description:
      'We turn your content into instant answers. Your AI agent stays fresh with automatic updates and works across chat and web.',
  },
  '/products/website-agent': {
    title: 'Website AI Agent',
    description:
      'Give visitors instant, helpful answers on your site. Your AI agent learns from your docs, pages, and media. No extra support needed.',
  },
  '/products/internal-agent': {
    title: 'Internal AI Agent',
    description:
      'Help your team find answers fast. Search knowledge bases, policies, and playbooks instantly without digging through emails or wikis.',
  },
  '/products/lead-gen': {
    title: 'Lead Generation Engine',
    description:
      'A prospect list engine for any market you sell into. We find your targets, research contact and credibility signals, and score each one so your team spends time selling, not searching.',
  },
  '/products/lead-gen/advisors': {
    title: 'Advisor Lead Generation',
    description:
      'We pull registered financial advisors from public SEC data, find contact and credibility signals, and score each one so your team knows who to call first without spending hours on research.',
  },
  '/products/openclaw-custom-development': {
    title: 'OpenClaw Custom Development Services',
    description:
      'Get OpenClaw systems tailored to your workflows, tools, and data. We design, build, and deploy custom capabilities your team can run in production.',
  },
  '/products/agentic-system-development': {
    title: 'Custom Agentic System Development',
    description:
      'Build agentic systems that reason through tasks, call tools safely, and deliver measurable outcomes. We help you ship with practical guardrails.',
  },
  '/products/mcp': {
    title: 'MCP Development Services',
    description:
      'Design and deploy custom MCP servers and integrations so your AI systems can securely connect to internal tools, APIs, and data sources.',
  },
  '/products/agent/demos': {
    title: 'AI Agent Demos',
    description:
      'See how AI agents work across real industries. Browse working demos that capture leads, answer questions, and handle bookings automatically.',
  },
  '/products/extraction-api': {
    title: 'OCR & Receipt Parsing API (PDF/Image)',
    description:
      'Extract text and structured data from PDFs and images. Deskew photos, run OCR, and parse receipts/invoices into JSON with production-friendly endpoints.',
    ogType: 'product',
  },
  '/products/extraction-api/deskew': {
    title: 'Straighten Photos Before Extraction',
    description:
      'Fix skewed, angled, and warped photos automatically. Get straighter images ready for accurate text extraction with one API call.',
  },
  '/products/extraction-api/parse-receipt': {
    title: 'Parse Receipts into JSON',
    description:
      'Turn receipt photos into usable data instantly. Get store name, items, totals, and taxes. Everything you need syncs straight into your accounting system.',
  },
  '/products/extraction-api/extract': {
    title: 'Extract Data from PDFs & Images (OCR API)',
    description:
      'OCR API that turns receipts, invoices, and documents—PDF or image—into structured JSON in one call. Production-ready with reliable, predictable results.',
  },
  '/products/extraction-api/docs': {
    title: 'Extraction API Docs (OCR + Receipt Parsing)',
    description:
      'API documentation for OCR, deskew, and receipt/invoice parsing. Learn authentication, request formats, response fields, and billing.',
  },
  '/products/extraction-api/docs/auth': {
    title: 'Authentication | Extraction API',
    description:
      'Set up API authentication with your keys and headers. Secure your extraction requests with best practices for production.',
  },
  '/products/extraction-api/docs/deskew': {
    title: 'Deskew API Endpoint (/v1/deskew)',
    description:
      'Straighten images programmatically. Learn the request format, response fields, and tips for getting cleaner scans.',
  },
  '/products/extraction-api/docs/parse': {
    title: 'Parse API Endpoint (/v1/parse/receipt)',
    description:
      'Receipt and invoice OCR parsing to structured JSON. See inputs, outputs, supported file types, and response fields.',
  },
  '/products/extraction-api/docs/billing': {
    title: 'Billing & Quotas | Extraction API',
    description:
      'Understand pricing, limits, and how billing works. Get details on API credits, rate limits, and plan options.',
  },
  '/products/extraction-api/walkthrough': {
    title: 'Receipt OCR Walkthrough (Deskew + Parse)',
    description:
      'See deskew + receipt parsing end-to-end. Real before/after images, API calls, and example JSON output you can copy.',
  },
  '/build-demo': {
    title: 'Get a Working AI Agent Demo',
    description:
      "We'll build a working AI agent for your site in under 24 hours. See how it works before you commit.",
  },
  '/book-demo': {
    title: 'Book an AI Automation Demo',
    description:
      'Talk to our team and see exactly how AI agents and extraction APIs can work for your business. No pressure, just answers.',
  },
  '/about': {
    title: 'About upCaret',
    description:
      'We help businesses automate support and extract data from documents. Learn about our mission to make AI simple and useful.',
  },
  '/contact': {
    title: 'Contact upCaret',
    description:
      "Tell us what you're trying to improve. We'll help you choose between a free call, the Automation Review, a one-off build, or ongoing support.",
  },
  '/contact/automation-review': {
    title: 'Request an Automation Review',
    description:
      'Request the $999 Automation Review. Tell us what feels slow, manual, expensive, or error-prone so we can confirm fit, scope, and the right AI setup.',
  },
  '/free-ai-audit': {
    title: "Free 15-Minute AI Audit: Find What's Costing You Time",
    description:
      'Book a free 15-minute AI audit call to find your biggest workflow bottleneck. Then a short second call recommends one AI tool you can start using right away.',
    ogImage: FREE_AI_AUDIT_OG_IMAGE,
    ogImageAlt:
      'upCaret free AI audit card: find the one workflow costing you the most, with a book your free audit call to action',
  },
  '/free-ai-audit-booking': {
    title: 'Book a Free 15-Minute AI Audit Call',
    description:
      'Book your free 15-minute AI audit call and choose a time instantly. We will meet on Google Meet and focus on your highest-impact workflow bottleneck.',
  },
  '/products/automation-review': {
    title: 'Automation Review: $999 Automation Report',
    description:
      '$999 Automation Review: 3–7 opportunities ranked by impact and effort, tool costs, ROI math, and a quick-wins plan. Report delivered within 3 business days.',
  },
  '/products/automation-support': {
    title: 'Automation Support: Monthly Help from a Real Engineer',
    description:
      'A $1,000–$2,000/month plan with a hands-on engineer: two 45-min working calls a month, unlimited async support, and a shared log of every automation we ship together.',
  },
  '/privacy': {
    title: 'Privacy Policy',
    description:
      'How we handle your data. We take privacy seriously and share our security practices and data handling.',
  },
  '/terms': {
    title: 'Terms of Service',
    description:
      'The terms and conditions for using upCaret. Fair, clear, and easy to understand.',
  },
};

function resolveEntry(pathname: string): SeoEntry & { canonical: string } {
  // TanStack Router + hosting may produce either "/about" or "/about/".
  // Keep lookup keys stable and canonicalize to NO trailing slash.
  const lookup = stripTrailingSlash(pathname);
  const entry = seoEntries[lookup] ?? defaultEntry;
  const canonical = `${SITE_URL}${lookup}`;
  return { ...entry, canonical, ogType: entry.ogType ?? defaultEntry.ogType };
}

export function buildHead(pathname: string) {
  const entry = resolveEntry(pathname);
  const ogType = entry.ogType ?? 'website';
  const ogImage = entry.ogImage ?? DEFAULT_OG_IMAGE;
  const ogImageAlt =
    entry.ogImageAlt ?? 'upCaret - Custom AI Agents & Automation';

  return {
    meta: [
      { title: entry.title },
      { name: 'description', content: entry.description },
      { property: 'og:title', content: entry.title },
      { property: 'og:description', content: entry.description },
      { property: 'og:url', content: entry.canonical },
      { property: 'og:type', content: ogType },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:type', content: 'image/png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      {
        property: 'og:image:alt',
        content: ogImageAlt,
      },
      { property: 'og:site_name', content: 'upCaret' },
      { property: 'og:locale', content: 'en_US' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: entry.title },
      { name: 'twitter:description', content: entry.description },
      { name: 'twitter:image', content: ogImage },
      {
        name: 'twitter:image:alt',
        content: ogImageAlt,
      },
    ],
    links: [{ rel: 'canonical', href: entry.canonical }],
  };
}

export function getSeoEntry(pathname: string) {
  return resolveEntry(pathname);
}

export function getDemoSeo(slug: string) {
  const config = DEMO_CONFIGS[slug];
  if (!config) return null;

  const title = `${config.title} | AI Agent Demo`;
  const description = config.description;
  const canonical = `${SITE_URL}/products/agent/demos/${slug}`;

  return {
    title,
    description,
    canonical,
    ogType: 'video.other',
  };
}

export function buildDemoHead(slug: string) {
  const entry = getDemoSeo(slug);
  if (!entry) return null;

  return {
    meta: [
      { title: entry.title },
      { name: 'description', content: entry.description },
      { property: 'og:title', content: entry.title },
      { property: 'og:description', content: entry.description },
      { property: 'og:url', content: entry.canonical },
      { property: 'og:type', content: entry.ogType ?? 'video.other' },
      { property: 'og:image', content: DEFAULT_OG_IMAGE },
      { property: 'og:image:type', content: 'image/png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      {
        property: 'og:image:alt',
        content: 'upCaret – Custom AI Agents & Automation',
      },
      { property: 'og:site_name', content: 'upCaret' },
      { property: 'og:locale', content: 'en_US' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: entry.title },
      { name: 'twitter:description', content: entry.description },
      { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      {
        name: 'twitter:image:alt',
        content: 'upCaret – Custom AI Agents & Automation',
      },
    ],
    links: [{ rel: 'canonical', href: entry.canonical }],
  };
}

export function buildNotFoundHead() {
  const title = 'Page not found';
  const description =
    'This page doesn’t exist on upcaret.com. Use the recovery router, search, or curated links to get back on track.';

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { name: 'robots', content: 'noindex, follow' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'upCaret' },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
  };
}

export { SITE_URL, seoEntries };
